/**
 * Length Role - Constants
 *
 * Fatores de conversão, símbolos e aliases para unidades de comprimento.
 *
 * Fontes:
 * - NIST SP 811 (2008)
 * - International Yard and Pound Agreement (1959)
 * - SI Brochure, 9th Edition (2019)
 */

import type { SimpleUnitConfig, UnitAliases } from "../../contracts";

// =============================================================================
// TYPES
// =============================================================================

export type LengthUnit =
  // SI
  | "kilometer"
  | "hectometer"
  | "decameter"
  | "meter"
  | "decimeter"
  | "centimeter"
  | "millimeter"
  | "micrometer"
  | "nanometer"
  // Imperial/US
  | "inch"
  | "foot"
  | "yard"
  | "mile"
  // Nautical
  | "nautical_mile"
  // Other
  | "fathom"
  | "furlong"
  | "league";

// =============================================================================
// UNITS CONFIG
// =============================================================================

/**
 * Configuração de cada unidade de comprimento.
 *
 * TODOS os fatores são EXATOS:
 * - SI: derivados de potências de 10
 * - Imperial: exatos desde International Yard and Pound Agreement (1959)
 *   - 1 yard = 0.9144 m (exato por definição)
 *   - 1 foot = 0.3048 m (= yard/3)
 *   - 1 inch = 0.0254 m (= foot/12)
 *   - 1 mile = 1609.344 m (= 5280 feet)
 */
export const LENGTH_UNITS: Record<LengthUnit, SimpleUnitConfig> = {
  // SI Units (todos exatos)
  kilometer: {
    factor: 1000,
    symbol: "km",
    singular: "kilometer",
    plural: "kilometers",
  },
  hectometer: {
    factor: 100,
    symbol: "hm",
    singular: "hectometer",
    plural: "hectometers",
  },
  decameter: {
    factor: 10,
    symbol: "dam",
    singular: "decameter",
    plural: "decameters",
  },
  meter: {
    factor: 1,
    symbol: "m",
    singular: "meter",
    plural: "meters",
  },
  decimeter: {
    factor: 0.1,
    symbol: "dm",
    singular: "decimeter",
    plural: "decimeters",
  },
  centimeter: {
    factor: 0.01,
    symbol: "cm",
    singular: "centimeter",
    plural: "centimeters",
  },
  millimeter: {
    factor: 0.001,
    symbol: "mm",
    singular: "millimeter",
    plural: "millimeters",
  },
  micrometer: {
    factor: 0.000001,
    symbol: "μm",
    singular: "micrometer",
    plural: "micrometers",
  },
  nanometer: {
    factor: 0.000000001,
    symbol: "nm",
    singular: "nanometer",
    plural: "nanometers",
  },

  // Imperial/US (exatos desde 1959)
  inch: {
    factor: 0.0254,
    symbol: "in",
    singular: "inch",
    plural: "inches",
  },
  foot: {
    factor: 0.3048,
    symbol: "ft",
    singular: "foot",
    plural: "feet",
  },
  yard: {
    factor: 0.9144,
    symbol: "yd",
    singular: "yard",
    plural: "yards",
  },
  mile: {
    factor: 1609.344,
    symbol: "mi",
    singular: "mile",
    plural: "miles",
  },

  // Nautical (exato por definição internacional)
  nautical_mile: {
    factor: 1852,
    symbol: "nmi",
    singular: "nautical mile",
    plural: "nautical miles",
  },

  // Other (derivados, portanto exatos)
  fathom: {
    factor: 1.8288, // 6 feet
    symbol: "ftm",
    singular: "fathom",
    plural: "fathoms",
  },
  furlong: {
    factor: 201.168, // 660 feet = 1/8 mile
    symbol: "fur",
    singular: "furlong",
    plural: "furlongs",
  },
  league: {
    factor: 4828.032, // 3 miles
    symbol: "lea",
    singular: "league",
    plural: "leagues",
  },
};

// =============================================================================
// ALIASES
// =============================================================================

/**
 * Aliases para reconhecimento de unidades no cast
 */
export const LENGTH_ALIASES: UnitAliases = {
  // kilometer
  km: "kilometer",
  // hectometer
  hm: "hectometer",
  // decameter
  dam: "decameter",
  // meter
  m: "meter",
  // decimeter
  dm: "decimeter",
  // centimeter
  cm: "centimeter",
  // millimeter
  mm: "millimeter",
  // micrometer
  "μm": "micrometer",
  um: "micrometer",
  // nanometer
  nm: "nanometer",
  // inch
  in: "inch",
  '"': "inch",
  // foot
  ft: "foot",
  "'": "foot",
  // yard
  yd: "yard",
  // mile
  mi: "mile",
  // nautical_mile
  nmi: "nautical_mile",
  // fathom
  ftm: "fathom",
  // furlong
  fur: "furlong",
  // league
  lea: "league",
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todas as unidades */
export const LENGTH_UNIT_LIST = Object.keys(LENGTH_UNITS) as LengthUnit[];

/** Fatores de conversão (para compatibilidade) */
export const LENGTH_FACTORS: Record<LengthUnit, number> = Object.fromEntries(
  Object.entries(LENGTH_UNITS).map(([unit, config]) => [
    unit,
    config.factor ?? 1,
  ]),
) as Record<LengthUnit, number>;
